import type { ReactNode } from 'react'
import {
  FBC_GRAMMAR,
  fbcChannelState,
  getFbcChannelLabel,
  type FbcChannelId,
  type FbcSignalState,
} from './fbc-design-grammar'
import { useLocaleContext } from '../../context/LocaleContext.js'

export { fbcChannelState }

function dotClass(state: FbcSignalState): string {
  if (state === 'signal') return 'bg-[var(--fc-accent)]'
  if (state === 'active') return 'bg-[var(--fc-accent)]/70'
  return 'bg-current opacity-40'
}

interface FbcDotProps {
  state?: FbcSignalState
  className?: string
}

export function FbcDot({ state = 'idle', className }: FbcDotProps) {
  return (
    <span
      className={`block h-1 w-1 shrink-0 rounded-full ${dotClass(state)} ${className ?? ''}`}
      aria-hidden="true"
    />
  )
}

interface FbcPatternProps {
  channel: FbcChannelId
  state?: FbcSignalState
  className?: string
}

export function FbcPattern({ channel, state = 'idle', className }: FbcPatternProps) {
  const active = state === 'signal' || state === 'active'

  switch (channel) {
    case 'signal':
      return (
        <span className={`grid grid-cols-2 gap-[2px] ${className ?? ''}`} aria-hidden="true">
          <FbcDot state={active ? 'signal' : 'idle'} />
          <FbcDot state={active ? 'active' : 'idle'} />
          <FbcDot state={active ? 'active' : 'idle'} />
          <FbcDot state={active ? 'signal' : 'idle'} />
        </span>
      )

    case 'matrix':
      return (
        <span className={`grid grid-cols-3 gap-[3px] ${className ?? ''}`} aria-hidden="true">
          {Array.from({ length: 9 }).map((_, i) => (
            <FbcDot key={i} state={i === 4 && active ? 'signal' : 'idle'} />
          ))}
        </span>
      )

    case 'voice':
      return (
        <span className={`flex h-4 items-end gap-[3px] ${className ?? ''}`} aria-hidden="true">
          {[5, 9, 14, 10, 6].map((h, i) => (
            <span
              key={h}
              className={`w-1 rounded-full ${i === 2 && active ? 'bg-[var(--fc-accent)]' : 'bg-current opacity-50'}`}
              style={{ height: h }}
            />
          ))}
        </span>
      )

    case 'vision':
      return (
        <span className={`relative block h-4 w-4 ${className ?? ''}`} aria-hidden="true">
          <FbcDot state={active ? 'signal' : 'idle'} className="absolute left-0.5 top-0.5" />
          <FbcDot state={active ? 'signal' : 'idle'} className="absolute right-0.5 top-0.5" />
          <FbcDot state={active ? 'active' : 'idle'} className="absolute left-1/2 top-[7px] -translate-x-1/2" />
          <FbcDot state={active ? 'active' : 'idle'} className="absolute bottom-0 left-1" />
          <FbcDot state={active ? 'active' : 'idle'} className="absolute bottom-0 right-1" />
        </span>
      )

    case 'screen':
      return (
        <span className={`grid grid-cols-3 grid-rows-3 gap-[2px] ${className ?? ''}`} aria-hidden="true">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <FbcDot
              key={i}
              state={i === 4 && active ? 'signal' : [0, 2, 6, 8].includes(i) && active ? 'active' : 'idle'}
            />
          ))}
        </span>
      )

    case 'memory':
      return (
        <span className={`flex flex-col gap-[3px] ${className ?? ''}`} aria-hidden="true">
          {[0, 1, 2].map(row => (
            <span key={row} className="flex gap-[3px]" style={{ marginLeft: row * 2 }}>
              <FbcDot state={active && row === 1 ? 'signal' : active ? 'active' : 'idle'} />
              <FbcDot state={active ? 'active' : 'idle'} />
            </span>
          ))}
        </span>
      )

    case 'research':
      return (
        <span className={`flex items-center gap-[3px] ${className ?? ''}`} aria-hidden="true">
          <FbcDot state={active ? 'active' : 'idle'} />
          <span className={`h-px w-2 ${active ? 'bg-[var(--fc-accent)]/55' : 'bg-current opacity-30'}`} />
          <FbcDot state={active ? 'signal' : 'idle'} />
          <span className={`h-px w-2 ${active ? 'bg-[var(--fc-accent)]/55' : 'bg-current opacity-30'}`} />
          <FbcDot state={active ? 'active' : 'idle'} />
        </span>
      )

    case 'export':
      return (
        <span className={`flex items-center gap-[2px] ${className ?? ''}`} aria-hidden="true">
          <FbcDot state={active ? 'active' : 'idle'} />
          <FbcDot state={active ? 'signal' : 'idle'} />
          <span className={`h-px w-3 ${active ? 'bg-[var(--fc-accent)]' : 'bg-current opacity-35'}`} />
          <FbcDot state={active ? 'signal' : 'idle'} />
          <FbcDot state={active ? 'active' : 'idle'} />
        </span>
      )

    case 'report':
      return (
        <span className={`flex flex-col gap-[3px] ${className ?? ''}`} aria-hidden="true">
          {[0, 1, 2, 3].map(i => (
            <span key={i} className="flex items-center gap-[3px]">
              <FbcDot state={active && i === 0 ? 'signal' : active ? 'active' : 'idle'} />
              <span className={`h-px w-2 ${active ? 'bg-current opacity-25' : 'bg-current opacity-15'}`} />
            </span>
          ))}
        </span>
      )

    case 'halt':
      return (
        <span className={`grid grid-cols-2 gap-[2px] ${className ?? ''}`} aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <FbcDot key={i} state={active ? 'signal' : 'idle'} />
          ))}
        </span>
      )

    case 'transmit':
      return (
        <span className={`flex items-center gap-[2px] ${className ?? ''}`} aria-hidden="true">
          <span className={`h-px w-2 ${active ? 'bg-[var(--fc-accent)]/45' : 'bg-current opacity-25'}`} />
          <FbcDot state={active ? 'active' : 'idle'} />
          <FbcDot state={active ? 'signal' : 'idle'} />
          <FbcDot state={active ? 'signal' : 'idle'} />
        </span>
      )

    case 'phase':
      return (
        <span className={`flex items-center gap-[3px] ${className ?? ''}`} aria-hidden="true">
          <FbcDot state={active ? 'signal' : 'idle'} />
          <span className="h-px w-1.5 bg-current opacity-25" />
          <FbcDot state={active ? 'active' : 'idle'} />
        </span>
      )
  }
}

interface FbcSignalLineProps {
  variant?: 'full' | 'short' | 'baseline'
  active?: boolean
  className?: string
}

export function FbcSignalLine({ variant = 'full', active = false, className }: FbcSignalLineProps) {
  const width =
    variant === 'short' ? 'w-8' : variant === 'baseline' ? 'w-full' : 'flex-1 min-w-[12px]'

  return (
    <span
      className={`fbc-signal-line inline-flex items-center gap-1 ${width} ${className ?? ''}`}
      aria-hidden="true"
    >
      {variant !== 'baseline' && <FbcDot state={active ? 'signal' : 'idle'} />}
      <span className={`fbc-signal-line-track h-px flex-1 ${active ? 'is-active' : ''}`} />
      {variant !== 'baseline' && <FbcDot state={active ? 'active' : 'idle'} />}
    </span>
  )
}

interface FbcStatusLabelProps {
  channel?: FbcChannelId
  state?: FbcSignalState
  children: ReactNode
  trailing?: boolean
  className?: string
}

export function FbcStatusLabel({
  channel = 'signal',
  state = 'idle',
  children,
  trailing = true,
  className,
}: FbcStatusLabelProps) {
  return (
    <span
      className={`inline-flex items-center gap-2 uppercase text-[var(--fc-text-muted)] ${className ?? ''}`}
      style={{
        fontSize: FBC_GRAMMAR.labelSize,
        letterSpacing: FBC_GRAMMAR.labelTracking,
      }}
    >
      <FbcPattern channel={channel} state={state} />
      <span className="text-[var(--fc-text)]">{children}</span>
      {trailing && <FbcDot state={state === 'idle' ? 'idle' : 'signal'} />}
    </span>
  )
}

interface FbcChannelControlProps {
  channel: FbcChannelId
  state?: FbcSignalState
  onClick?: () => void
  showLabel?: boolean
  compact?: boolean
  className?: string
}

export function FbcChannelControl({
  channel,
  state = 'idle',
  onClick,
  showLabel = false,
  compact = false,
  className,
}: FbcChannelControlProps) {
  const { t } = useLocaleContext()
  const label = getFbcChannelLabel(channel, t)
  const active = state === 'signal' || state === 'active'

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`fbc-channel group flex flex-col items-center gap-1 rounded-[14px] bg-[var(--fc-control-bg)] ring-1 ring-[var(--fc-glass-ring)] transition-colors hover:ring-[var(--fc-accent)]/45 ${compact ? 'h-9 w-9 justify-center px-0' : 'h-10 min-w-[2.5rem] justify-center px-1.5 sm:h-11 sm:min-w-[2.75rem]'} ${active ? 'is-active' : ''} ${className ?? ''}`}
    >
      <FbcPattern channel={channel} state={state} />
      {showLabel && (
        <span
          className="hidden text-[7px] uppercase tracking-[0.14em] text-[var(--fc-text-muted)] group-hover:text-[var(--fc-text)] sm:block"
        >
          {label}
        </span>
      )}
    </button>
  )
}

interface FbcSignalFieldProps {
  id: string
  placeholder?: string
  value?: string
  readOnly?: boolean
}

export function FbcSignalField({
  id,
  placeholder = 'Transmit to intelligence layer...',
  value = '',
  readOnly = true,
}: FbcSignalFieldProps) {
  return (
    <div className="fbc-signal-field relative min-w-0 flex-1">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex flex-col items-center justify-center gap-[3px] pl-2" aria-hidden="true">
        <FbcDot state="active" />
        <span className="fbc-signal-field-rail w-px flex-1 min-h-[12px]" />
        <FbcDot state="signal" />
      </div>

      <label className="sr-only" htmlFor={id}>
        Intelligence command input
      </label>
      <input
        id={id}
        type="text"
        readOnly={readOnly}
        value={value}
        placeholder={placeholder}
        className="h-10 w-full bg-transparent pl-7 pr-2 font-mono text-[13px] tracking-[0.04em] text-[var(--fc-text)] outline-none placeholder:text-[var(--fc-text-muted)] sm:h-11 sm:pl-8 sm:text-[14px]"
      />

      <div className="pointer-events-none absolute bottom-1.5 left-7 right-2 sm:left-8" aria-hidden="true">
        <FbcSignalLine variant="baseline" active />
      </div>

      <span className="fbc-signal-cursor pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" aria-hidden="true">
        <FbcDot state="signal" />
      </span>
    </div>
  )
}

interface FbcStatusChipProps {
  channel: FbcChannelId
  state?: FbcSignalState
  className?: string
}

export function FbcStatusChip({ channel, state = 'signal', className }: FbcStatusChipProps) {
  const { t } = useLocaleContext()
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full bg-[var(--fc-glass)] px-2.5 py-1.5 ring-1 ring-[var(--fc-glass-ring)] backdrop-blur-md ${className ?? ''}`}
    >
      <FbcStatusLabel channel={channel} state={state} trailing={false}>
        {getFbcChannelLabel(channel, t, 'status')}
      </FbcStatusLabel>
      <FbcDot state="signal" />
    </span>
  )
}

export function FbcFindingIndex({ index }: { index: number }) {
  const label = String(index + 1).padStart(2, '0')
  return (
    <span className="flex items-center gap-1.5 pt-0.5 font-mono text-[12px] font-semibold tabular-nums text-[var(--fc-accent)]">
      <FbcPattern channel="report" state="signal" />
      {label}
    </span>
  )
}
